"use client";

/**
 * EquityCurveChart — the "$10k over time" curve. Uses lightweight-charts
 * AreaSeries (same lib as the trade chart) for reliable rendering. Static
 * data, no network. Green when the curve ends up, red when down.
 */

import { useEffect, useRef } from "react";
import { createChart, AreaSeries, type IChartApi, type UTCTimestamp } from "lightweight-charts";
import type { EquityPoint } from "@/lib/mock/signal-performance";

export function EquityCurveChart({
  data,
  height = 220,
}: {
  data: EquityPoint[];
  height?: number;
}) {
  const containerRef = useRef<HTMLDivElement>(null);
  const chartRef = useRef<IChartApi | null>(null);

  useEffect(() => {
    if (!containerRef.current || data.length === 0) return;

    // lightweight-charts requires strictly-ascending UNIQUE timestamps.
    // The live curve has one point per closed trade, so days with multiple
    // closes produce duplicate dates and the chart silently renders empty.
    // Collapse to one point per day (last value wins = end-of-day equity).
    const byDay = new Map<string, number>();
    for (const d of data) byDay.set(d.date, d.value);
    const clean = Array.from(byDay.entries())
      .map(([date, value]) => ({ date, value }))
      .sort((a, b) => (a.date < b.date ? -1 : 1));

    const start = clean[0].value;
    const end = clean[clean.length - 1].value;
    const up = end >= start;
    const line = up ? "#22c55e" : "#f87171";

    const chart = createChart(containerRef.current, {
      width: containerRef.current.clientWidth,
      height,
      layout: { background: { color: "transparent" }, textColor: "#e2e8f0", fontSize: 11 },
      grid: {
        vertLines: { color: "rgba(148,163,184,0.05)" },
        horzLines: { color: "rgba(148,163,184,0.05)" },
      },
      crosshair: { mode: 0 },
      rightPriceScale: { borderColor: "rgba(148,163,184,0.12)" },
      timeScale: { borderColor: "rgba(148,163,184,0.12)", fixLeftEdge: true, fixRightEdge: true },
    });
    chartRef.current = chart;

    const series = chart.addSeries(AreaSeries, {
      lineColor: line,
      lineWidth: 2,
      topColor: up ? "rgba(34,197,94,0.28)" : "rgba(248,113,113,0.28)",
      bottomColor: "rgba(0,0,0,0)",
      priceFormat: { type: "price", precision: 0, minMove: 1 },
    });
    series.setData(
      clean.map((d) => ({
        time: Math.floor(new Date(d.date).getTime() / 1000) as UTCTimestamp,
        value: d.value,
      })),
    );
    chart.timeScale().fitContent();

    const ro = new ResizeObserver(() => {
      if (containerRef.current) chart.applyOptions({ width: containerRef.current.clientWidth });
    });
    ro.observe(containerRef.current);

    return () => {
      ro.disconnect();
      chart.remove();
      chartRef.current = null;
    };
  }, [data, height]);

  return (
    <div className="relative rounded-sm border border-white/10 bg-black/40 overflow-hidden">
      <div ref={containerRef} style={{ height }} />
    </div>
  );
}
